import { authenticatedApi } from '../../../shared/api/apiClientUtils.ts';

// DTOs matching the backend CreateGangRequest
export interface CreateGangRequest {
  name: string;
  isEqualSplit: boolean;
  members: CreateGangMemberRequest[];
}

export interface CreateGangMemberRequest {
  userId: string;
  isLeader: boolean;
  canSeeSplit: boolean;
  defaultSplitPercentage?: number;
  defaultRate?: number;
  defaultUnitsValue?: number;
}

export interface CreateGangResponse {
  id: string;
  name: string;
  members: CreateGangMemberResponse[];
}

export interface UpdateGangRequest {
  gangId: string;
  name: string;
  isEqualSplit: boolean;
  members: CreateGangMemberRequest[];
}

export interface UpdateGangResponse {
  id: string;
  name: string;
  members: CreateGangMemberResponse[];
}

export interface CreateGangMemberResponse {
  userId: string;
  isLeader: boolean;
  canSeeSplit: boolean;
  defaultSplitPercentage?: number;
  defaultRate?: number;
  defaultUnitsValue?: number;
}

export interface ValidateGangNameResponse {
  isValid: boolean;
  message?: string;
}

export interface ListGangsResponse {
  gangs: Array<{
    id: string;
    name: string;
    isEqualSplit: boolean;
    isDeleted: boolean;
    members: Array<{
      userId: string;
      givenName?: string | null;
      familyName?: string | null;
      email?: string | null;
      isLeader: boolean;
      canSeeSplit?: boolean | null;
      defaultSplitPercentage?: number | null;
      defaultRate?: number | null;
      defaultUnitsValue?: number | null;
    }>;
  }>;
}

/**
 * Fetch gangs for the current organisation, optionally filtered by search term.
 */
export const listGangs = async (
  searchTerm?: string
): Promise<ListGangsResponse> => {
  const response = await authenticatedApi.get<ListGangsResponse>(
    '/api/v1/gangs/list',
    {
      params: searchTerm ? { searchTerm } : undefined,
    }
  );
  return response.data;
};

/**
 * Validate if a gang name is available
 */
export const validateGangName = async (
  name: string
): Promise<ValidateGangNameResponse> => {
  const response = await authenticatedApi.get<ValidateGangNameResponse>(
    '/api/v1/gangs/validate',
    {
      params: { name },
    }
  );
  return response.data;
};

/**
 * Create a new gang
 */
export const createGang = async (
  request: CreateGangRequest
): Promise<CreateGangResponse> => {
  const response = await authenticatedApi.post<CreateGangResponse>(
    '/api/v1/gangs/create',
    request
  );
  return response.data;
};

/**
 * Delete (soft-delete) a gang
 */
export const deleteGang = async (gangId: string): Promise<void> => {
  await authenticatedApi.delete(`/api/v1/gangs/${gangId}`);
};

/**
 * Update an existing gang
 */
export const updateGang = async (
  request: UpdateGangRequest
): Promise<UpdateGangResponse> => {
  const response = await authenticatedApi.put<UpdateGangResponse>(
    '/api/v1/gangs/update',
    request
  );
  return response.data;
};
